'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import { CloudUpload, TriangleAlert } from 'lucide-react';
import { useBackgroundUploadJobs } from '@/contexts/background-uploads-context';
import { UPLOAD_ATTENTION_TONE_CLASS } from '@/components/org-chat/pending-upload-send/pending-upload-send-file-row';
import { useNetworkStatus } from '@/hooks/use-network-status';
import {
  formatPendingUploadCount,
  getPendingUploadSendCopy,
} from '@/i18n/pending-upload-send-copy';
import { threadRoutePath } from '@/lib/thread-route-prefetch';
import { formatComposerUploadProgress } from '@/lib/uploads/composer-upload-progress';
import { cn } from '@/lib/utils';
import { getBackgroundUploadIndicatorState } from './background-uploads-indicator-state';
import { useLocalUploadBytes } from './use-local-upload-bytes';

/** The route segment after `/threads/`, when the member is on a thread. */
function currentThreadRef(pathname: string | null) {
  return pathname?.match(/\/threads\/([^/?#]+)/)?.[1] ?? null;
}

/**
 * App-wide pill while files a send took with it are still uploading
 * (#4844): "Uploading 3 files in the background · 42%", linking to the task.
 * Hidden on that task's own page, whose waiting message says the same.
 */
export function BackgroundUploadsIndicator() {
  const locale = useLocale();
  const pathname = usePathname();
  const isOnline = useNetworkStatus();
  const jobs = useBackgroundUploadJobs();
  const loadedBytes = useLocalUploadBytes(jobs.map((job) => job.uploadId));
  const state = getBackgroundUploadIndicatorState({
    jobs,
    loadedBytes,
    currentThreadRef: currentThreadRef(pathname),
  });
  if (!state) return null;
  const copy = getPendingUploadSendCopy(locale);
  const needsAttention = state.status === 'needs_attention';
  const finishing = state.status === 'finishing';
  const label = formatPendingUploadCount(
    needsAttention
      ? copy.backgroundNeedsAttention
      : finishing
        ? copy.backgroundFinishing
        : copy.backgroundUploading,
    state.count,
    locale
  );
  const { percent } = formatComposerUploadProgress(
    { loadedBytes: state.loadedBytes, totalBytes: state.totalBytes },
    locale
  );
  const Icon = needsAttention ? TriangleAlert : CloudUpload;
  return (
    <div
      data-background-uploads-indicator
      className={cn(
        'pointer-events-none fixed inset-x-0 z-[90] flex justify-center px-2',
        // Inside the header lane (its h-8 controls sit 4px down), clear of
        // the divider and the first transcript line; below the offline
        // banner when that takes the top.
        isOnline ? 'top-1' : 'top-16'
      )}
      role="status"
      aria-live="polite"
    >
      <Link
        href={threadRoutePath(state.target.orgSlug, state.target.threadRef)}
        aria-label={`${label}. ${copy.openTask}`}
        className={cn(
          'imai-swap-in pointer-events-auto flex h-8 max-w-[calc(100vw-1rem)] min-w-0 items-center gap-2 rounded-full px-3',
          'bg-popover text-popover-foreground shadow-popover text-xs',
          'focus-visible:ring-ring/50 outline-none focus-visible:ring-2'
        )}
      >
        <Icon
          className={cn(
            'size-3.5 shrink-0',
            needsAttention
              ? UPLOAD_ATTENTION_TONE_CLASS
              : 'text-muted-foreground'
          )}
          aria-hidden
        />
        <span className="min-w-0 truncate">{label}</span>
        {state.status === 'uploading' && state.totalBytes > 0 ? (
          <span className="text-muted-foreground shrink-0 tabular-nums">
            <span aria-hidden>· </span>
            <span className="inline-block min-w-[4ch] text-end">{percent}</span>
          </span>
        ) : null}
      </Link>
    </div>
  );
}
